import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight2 } from "iconsax-react";
import { Button, Table } from "@/shared/ui";
import type { TaskListItem, TaskStatus } from "../../../types";
import type { ProjectBoardView } from "../../../hooks/useCrossProjectBoard";
import { COLUMN_HEADER_STYLES } from "../../../consts";
import StatusGroup from "./StatusGroup";

interface TasksListViewProps {
  projectBoards: ProjectBoardView[];
  onTaskClick: (task: TaskListItem, projectId: number) => void;
  onCreateTask?: (status: TaskStatus, projectId: number) => void;
  onNavigateToProject?: (projectId: number) => void;
}

type SortKey = "id" | "title" | "assigned_to_name" | "due_date" | "priority";
type SortDirection = "asc" | "desc";

interface SortConfig {
  key: SortKey;
  direction: SortDirection;
}

const STATUS_ORDER: TaskStatus[] = ["todo", "in_progress", "blocked", "done"];
const PRIORITY_ORDER: Record<string, number> = { low: 0, normal: 1, high: 2, critical: 3 };

export default function TasksListView({
  projectBoards,
  onTaskClick,
  onCreateTask,
  onNavigateToProject,
}: TasksListViewProps) {
  const { t } = useTranslation("BoardPage");
  const { t: tTasks } = useTranslation("TasksPage");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const showProjectHeadings = projectBoards.length > 1;

  function toggleSection(key: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function handleSort(key: SortKey) {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return prev.direction === "asc" ? { key, direction: "desc" } : null;
      }
      return { key, direction: "asc" };
    });
  }

  const sortTasks = useCallback(
    function sortTasks(tasks: TaskListItem[]): TaskListItem[] {
      if (!sortConfig) return tasks;
      const { key, direction } = sortConfig;
      const sorted = [...tasks].sort((a, b) => {
        let cmp = 0;
        if (key === "id") {
          cmp = a.id - b.id;
        } else if (key === "title") {
          cmp = a.title.localeCompare(b.title);
        } else if (key === "assigned_to_name") {
          cmp = (a.assigned_to_name ?? "").localeCompare(b.assigned_to_name ?? "");
        } else if (key === "due_date") {
          const da = a.due_date ? new Date(a.due_date).getTime() : 0;
          const db = b.due_date ? new Date(b.due_date).getTime() : 0;
          cmp = da - db;
        } else if (key === "priority") {
          cmp = (PRIORITY_ORDER[a.priority] ?? 0) - (PRIORITY_ORDER[b.priority] ?? 0);
        }
        return direction === "asc" ? cmp : -cmp;
      });
      return sorted;
    },
    [sortConfig],
  );

  function renderTable(board: ProjectBoardView | null, showHeading: boolean) {
    const projectId = board?.project.id ?? 0;
    return (
      <div key={projectId}>
        {showHeading && board && (
          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className="text-display-2xs content-base-primary">{board.project.name}</h3>
            {onNavigateToProject && (
              <Button
                variant="secondary"
                isIconButton
                className="w-5! h-5! p-0! radius-xs flex justify-center items-center content-base-secondary"
                onClick={() => onNavigateToProject(board.project.id)}>
                <ArrowRight2 size={12} color="currentColor" />
              </Button>
            )}
          </div>
        )}
        <Table.Table scrollable className="surface-light-stroke">
          <Table.Header className="surface-light-stroke">
            <tr>
              <Table.HeadCell
                sortable
                sortDirection={sortConfig?.key === "id" ? sortConfig.direction : undefined}
                onSort={() => handleSort("id")}
                width="w-20">
                {t("list.columns.number")}
              </Table.HeadCell>
              <Table.HeadCell
                sortable
                sortDirection={sortConfig?.key === "title" ? sortConfig.direction : undefined}
                onSort={() => handleSort("title")}>
                {t("list.columns.title")}
              </Table.HeadCell>
              <Table.HeadCell
                sortable
                sortDirection={sortConfig?.key === "assigned_to_name" ? sortConfig.direction : undefined}
                onSort={() => handleSort("assigned_to_name")}
                width="w-60">
                {t("list.columns.assignee")}
              </Table.HeadCell>
              <Table.HeadCell
                sortable
                sortDirection={sortConfig?.key === "due_date" ? sortConfig.direction : undefined}
                onSort={() => handleSort("due_date")}
                width="w-50">
                {t("list.columns.dueDate")}
              </Table.HeadCell>
              <Table.HeadCell
                sortable
                sortDirection={sortConfig?.key === "priority" ? sortConfig.direction : undefined}
                onSort={() => handleSort("priority")}
                width="w-40">
                {t("list.columns.priority")}
              </Table.HeadCell>
            </tr>
          </Table.Header>
          <Table.Body>
            {STATUS_ORDER.map((status) => {
              const column = board?.columns.find((col) => col.status === status);
              const tasks = column?.tasks ?? [];
              const sectionKey = `${projectId}-${status}`;
              const isCollapsed =
                tasks.length === 0 ? !collapsedSections.has(sectionKey) : collapsedSections.has(sectionKey);
              const styles = COLUMN_HEADER_STYLES[status];

              return (
                <StatusGroup
                  key={status}
                  status={status}
                  tasks={sortTasks(tasks)}
                  isCollapsed={isCollapsed}
                  styles={styles}
                  projectId={projectId}
                  onToggle={() => toggleSection(sectionKey)}
                  onTaskClick={onTaskClick}
                  onCreateTask={onCreateTask}
                  t={t}
                  tTasks={tTasks}
                />
              );
            })}
          </Table.Body>
        </Table.Table>
      </div>
    );
  }

  if (projectBoards.length === 0) {
    return <div className="space-y-6">{renderTable(null, false)}</div>;
  }

  return (
    <div className="space-y-6">
      {projectBoards.map((board) => renderTable(board, showProjectHeadings))}
    </div>
  );
}
